/**
 * Halaman Konten Website.
 *
 * Satu tempat untuk menulis berita/pengumuman/agenda/galeri/halaman yang
 * tampil di abduljalal.org. Alur kerja: draf -> terbit (butuh izin
 * `content:publish`) -> arsip. Slug tidak berubah saat judul diedit agar
 * tautan yang sudah tersebar tetap hidup.
 */
import { useMemo, useState } from 'react'
import {
  Badge,
  Button,
  Card,
  Checkbox,
  DataTable,
  Input,
  KolomForm,
  Modal,
  PageHeader,
  Select,
  StatCard,
  Textarea,
  type KolomTabel,
} from '@/components'
import { api, unggah, pesanKesalahan } from '@/lib/api'
import { formatTanggal } from '@/lib/format'
import { useAksi, useApi, usePaged } from '@/lib/useApi'
import { useOpsiUnit } from '@/lib/opsi'

interface MediaKonten {
  id: string
  publicUrl: string
  alt: string | null
  originalName?: string | null
  fileName: string
}

interface Kategori {
  id: string
  name: string
}

interface Konten {
  id: string
  title: string
  slug: string
  type: string
  status: string
  excerpt: string | null
  body: string
  tags: string | null
  isFeatured: boolean
  publishedAt: string | null
  createdAt: string
  category: Kategori | null
  cover: MediaKonten | null
}

interface Statistik {
  draft: number
  review: number
  published: number
  archived: number
  media: number
  pendaftarBaru: number
}

const TIPE = [
  { value: 'BERITA', label: 'Berita' },
  { value: 'PENGUMUMAN', label: 'Pengumuman' },
  { value: 'AGENDA', label: 'Agenda' },
  { value: 'GALERI', label: 'Galeri' },
  { value: 'HALAMAN', label: 'Halaman' },
]

const NADA_STATUS: Record<string, 'netral' | 'peringatan' | 'sukses' | 'info'> = {
  DRAFT: 'netral',
  REVIEW: 'peringatan',
  PUBLISHED: 'sukses',
  ARCHIVED: 'info',
}

const LABEL_STATUS: Record<string, string> = {
  DRAFT: 'Draf',
  REVIEW: 'Ditinjau',
  PUBLISHED: 'Terbit',
  ARCHIVED: 'Arsip',
}

const FORM_KOSONG = {
  id: '',
  type: 'BERITA',
  title: '',
  slug: '',
  excerpt: '',
  body: '',
  categoryId: '',
  tags: '',
  isFeatured: false,
  coverMediaId: '',
}

export default function Konten() {
  const opsiUnit = useOpsiUnit()
  const aksi = useAksi()

  const [cari, setCari] = useState('')
  const [tipe, setTipe] = useState('')
  const [status, setStatus] = useState('')
  const [editorTerbuka, setEditorTerbuka] = useState(false)
  const [form, setForm] = useState({ ...FORM_KOSONG })
  const [galatForm, setGalatForm] = useState<string | null>(null)
  const [sedangUnggah, setSedangUnggah] = useState(false)

  const statistik = useApi<Statistik>('/content/stats')
  const kategori = useApi<Kategori[]>('/content-categories')
  const media = useApi<MediaKonten[]>('/media', { perPage: 60 })

  const daftar = usePaged<Konten>('/content', {
    query: {
      q: cari.trim() || undefined,
      type: tipe || undefined,
      status: status || undefined,
    },
  })

  const opsiKategori = useMemo(
    () => (kategori.data ?? []).map((k) => ({ value: k.id, label: k.name })),
    [kategori.data],
  )

  const bukaBaru = () => {
    setForm({ ...FORM_KOSONG })
    setGalatForm(null)
    setEditorTerbuka(true)
  }

  const bukaUbah = (b: Konten) => {
    setForm({
      id: b.id,
      type: b.type,
      title: b.title,
      slug: b.slug,
      excerpt: b.excerpt ?? '',
      body: b.body ?? '',
      categoryId: b.category?.id ?? '',
      tags: b.tags ?? '',
      isFeatured: b.isFeatured,
      coverMediaId: b.cover?.id ?? '',
    })
    setGalatForm(null)
    setEditorTerbuka(true)
  }

  const simpan = async () => {
    setGalatForm(null)
    if (form.title.trim().length < 3) return setGalatForm('Judul minimal 3 karakter.')
    if (form.body.trim().length === 0) return setGalatForm('Isi konten wajib diisi.')

    const isi = {
      type: form.type,
      title: form.title.trim(),
      excerpt: form.excerpt.trim() || undefined,
      body: form.body,
      categoryId: form.categoryId || null,
      tags: form.tags.trim() || undefined,
      isFeatured: form.isFeatured,
      coverMediaId: form.coverMediaId || null,
    }

    const hasil = form.id
      ? await aksi.jalankan(() => api.patch<Konten>(`/content/${form.id}`, isi), {
          sukses: 'Konten tersimpan.',
          setelah: () => {
            setEditorTerbuka(false)
            daftar.muat()
            statistik.muat()
          },
        })
      : await aksi.jalankan(() => api.post<Konten>('/content', isi), {
          sukses: 'Draf konten dibuat.',
          setelah: () => {
            setEditorTerbuka(false)
            daftar.muat()
            statistik.muat()
          },
        })

    if (!hasil) setGalatForm(aksi.galat ?? 'Gagal menyimpan konten.')
  }

  const unggahSampul = async (berkas: File) => {
    setSedangUnggah(true)
    setGalatForm(null)
    try {
      const fd = new FormData()
      fd.append('file', berkas)
      const hasil = await unggah<MediaKonten>('/media', fd)
      setForm((f) => ({ ...f, coverMediaId: hasil.id }))
      media.muat()
    } catch (e) {
      setGalatForm(pesanKesalahan(e))
    } finally {
      setSedangUnggah(false)
    }
  }

  const terbitkan = (b: Konten) =>
    aksi.jalankan(() => api.post(`/content/${b.id}/publish`), {
      sukses: 'Konten diterbitkan.',
      setelah: () => {
        daftar.muat()
        statistik.muat()
      },
    })

  const tarik = (b: Konten) =>
    aksi.jalankan(() => api.post(`/content/${b.id}/unpublish`), {
      sukses: 'Konten ditarik ke draf.',
      setelah: () => {
        daftar.muat()
        statistik.muat()
      },
    })

  const arsipkan = (b: Konten) =>
    aksi.jalankan(() => api.post(`/content/${b.id}/archive`), {
      sukses: 'Konten diarsipkan.',
      setelah: () => {
        daftar.muat()
        statistik.muat()
      },
    })

  const hapus = (b: Konten) =>
    aksi.jalankan(() => api.delete(`/content/${b.id}`), {
      sukses: 'Draf dihapus.',
      setelah: () => {
        daftar.muat()
        statistik.muat()
      },
    })

  const kolom: KolomTabel<Konten>[] = [
    {
      kunci: 'title',
      judul: 'Judul',
      render: (b) => (
        <div className="flex items-center gap-3">
          {b.cover ? (
            <img
              src={b.cover.publicUrl}
              alt={b.cover.alt ?? ''}
              className="h-10 w-14 rounded object-cover"
              loading="lazy"
            />
          ) : (
            <div className="h-10 w-14 rounded bg-netral-100" />
          )}
          <div>
            <p className="font-medium text-netral-800">{b.title}</p>
            <p className="font-mono text-xs text-netral-500">/{b.slug}</p>
          </div>
        </div>
      ),
    },
    { kunci: 'type', judul: 'Tipe', render: (b) => TIPE.find((t) => t.value === b.type)?.label ?? b.type },
    { kunci: 'kategori', judul: 'Kategori', render: (b) => b.category?.name ?? '—' },
    {
      kunci: 'status',
      judul: 'Status',
      render: (b) => <Badge nada={NADA_STATUS[b.status] ?? 'netral'}>{LABEL_STATUS[b.status] ?? b.status}</Badge>,
    },
    {
      kunci: 'publishedAt',
      judul: 'Terbit',
      render: (b) => (b.publishedAt ? formatTanggal(b.publishedAt) : '—'),
    },
    {
      kunci: 'aksi',
      judul: '',
      sortable: false,
      render: (b) => (
        <div className="flex flex-wrap gap-1">
          <Button ukuran="kecil" varian="garis" onClick={() => bukaUbah(b)}>
            Ubah
          </Button>
          {b.status === 'PUBLISHED' ? (
            <Button ukuran="kecil" varian="halus" onClick={() => tarik(b)}>
              Tarik
            </Button>
          ) : b.status !== 'ARCHIVED' ? (
            <Button ukuran="kecil" onClick={() => terbitkan(b)}>
              Terbitkan
            </Button>
          ) : null}
          {b.status === 'DRAFT' ? (
            <Button ukuran="kecil" varian="bahaya" onClick={() => hapus(b)}>
              Hapus
            </Button>
          ) : b.status !== 'ARCHIVED' ? (
            <Button ukuran="kecil" varian="garis" onClick={() => arsipkan(b)}>
              Arsip
            </Button>
          ) : null}
        </div>
      ),
    },
  ]

  const s = statistik.data

  return (
    <div>
      <PageHeader
        judul="Konten Website"
        deskripsi="Berita, pengumuman, agenda, galeri, dan halaman statis yang tampil di abduljalal.org."
        aksi={
          <Button onClick={bukaBaru} ikon={<span aria-hidden>＋</span>}>
            Tulis Konten
          </Button>
        }
      />

      <div className="mb-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard label="Terbit" nilai={s?.published ?? 0} sublabel="tampil di situs" />
        <StatCard label="Draf" nilai={s?.draft ?? 0} sublabel="belum diterbitkan" />
        <StatCard label="Arsip" nilai={s?.archived ?? 0} sublabel="disimpan, tidak tampil" />
        <StatCard label="Media" nilai={s?.media ?? 0} sublabel="gambar di perpustakaan" />
      </div>

      <Card className="mb-4">
        <div className="grid gap-3 sm:grid-cols-3">
          <Input
            label="Cari"
            placeholder="Judul, ringkasan, atau tag"
            value={cari}
            onChange={(e) => setCari(e.target.value)}
          />
          <Select
            label="Tipe"
            placeholder="Semua tipe"
            opsi={TIPE}
            value={tipe}
            onChange={(e) => setTipe(e.target.value)}
          />
          <Select
            label="Status"
            placeholder="Semua status"
            opsi={Object.entries(LABEL_STATUS).map(([value, label]) => ({ value, label }))}
            value={status}
            onChange={(e) => setStatus(e.target.value)}
          />
        </div>
      </Card>

      <DataTable<Konten>
        kolom={kolom}
        data={daftar.items}
        kunciBaris={(b) => b.id}
        memuat={daftar.memuat}
        galat={daftar.galat}
        kodeGalat={daftar.kodeGalat}
        onCoba={daftar.muat}
        meta={daftar.meta}
        onHalaman={daftar.setHalaman}
        pesanKosong="Belum ada konten"
        deskripsiKosong="Mulai dengan menulis berita atau pengumuman pertama."
        aksiKosong={<Button onClick={bukaBaru}>Tulis Konten</Button>}
      />

      <Modal
        terbuka={editorTerbuka}
        onTutup={() => setEditorTerbuka(false)}
        judul={form.id ? 'Ubah Konten' : 'Tulis Konten Baru'}
        deskripsi="Isi konten mendukung HTML sederhana (paragraf, daftar, tautan, gambar). Tag skrip dibuang otomatis."
        lebar="max-w-3xl"
        footer={
          <div className="flex justify-end gap-2">
            <Button varian="garis" onClick={() => setEditorTerbuka(false)}>
              Batal
            </Button>
            <Button onClick={simpan} memuat={aksi.memuat}>
              Simpan
            </Button>
          </div>
        }
      >
        <div className="space-y-4">
          {galatForm ? (
            <p className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{galatForm}</p>
          ) : null}

          <KolomForm>
            <Select
              label="Tipe konten"
              opsi={TIPE}
              value={form.type}
              onChange={(e) => setForm({ ...form, type: e.target.value })}
            />
            <Select
              label="Kategori"
              placeholder="Tanpa kategori"
              opsi={opsiKategori}
              value={form.categoryId}
              onChange={(e) => setForm({ ...form, categoryId: e.target.value })}
            />
          </KolomForm>

          <Input
            label="Judul"
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            placeholder="Contoh: Santri Al Muflihun Juara MHQ Tingkat Kota"
          />

          {form.slug ? (
            <p className="text-xs text-netral-500">
              Tautan publik: <span className="font-mono">abduljalal.org/berita/{form.slug}</span> — slug tidak
              berubah saat judul diedit.
            </p>
          ) : null}

          <Textarea
            label="Ringkasan"
            bantuan="Tampil di kartu berita dan hasil pencarian. Kosongkan agar dibuat otomatis."
            value={form.excerpt}
            onChange={(e) => setForm({ ...form, excerpt: e.target.value })}
            rows={2}
          />

          <Textarea
            label="Isi konten"
            bantuan="Boleh HTML sederhana: <p>, <h2>, <ul>, <strong>, <a>, <img>."
            value={form.body}
            onChange={(e) => setForm({ ...form, body: e.target.value })}
            rows={10}
          />

          <KolomForm>
            <Input
              label="Tag"
              bantuan="Pisahkan dengan koma."
              value={form.tags}
              onChange={(e) => setForm({ ...form, tags: e.target.value })}
              placeholder="prestasi,tahfidz"
            />
            <div>
              <label className="mb-1 block text-sm font-medium text-netral-700">Gambar sampul</label>
              <div className="flex items-center gap-3">
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/gif,image/avif"
                  className="block w-full text-sm text-netral-600"
                  onChange={(e) => {
                    const f = e.target.files?.[0]
                    if (f) void unggahSampul(f)
                  }}
                />
                {sedangUnggah ? <span className="text-xs text-netral-500">Mengunggah…</span> : null}
              </div>
              {form.coverMediaId ? (
                <div className="mt-2 flex items-center gap-2">
                  <img
                    src={(media.data ?? []).find((m) => m.id === form.coverMediaId)?.publicUrl ?? ''}
                    alt=""
                    className="h-12 w-16 rounded object-cover"
                  />
                  <Button ukuran="kecil" varian="garis" onClick={() => setForm({ ...form, coverMediaId: '' })}>
                    Lepas
                  </Button>
                </div>
              ) : null}
            </div>
          </KolomForm>

          <Checkbox
            label="Jadikan unggulan (tampil paling atas di beranda)"
            checked={form.isFeatured}
            onChange={(e) => setForm({ ...form, isFeatured: e.target.checked })}
          />

          {opsiUnit.length > 1 ? (
            <p className="text-xs text-netral-500">
              Konten disimpan pada unit aktif Anda. Untuk unit lain, ganti unit aktif lewat menu pengguna.
            </p>
          ) : null}
        </div>
      </Modal>
    </div>
  )
}
